/**
 * MissionManagerView - Full CRUD for managers to manage missions
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, RefreshCw, Filter, Map, UserPlus, Eye,
  Trash2, Edit2, Download, ChevronRight
} from 'lucide-react';
import { missionAPI } from '../../../api/missions.js';
import { API_BASE_URL, getAuthHeaders } from '../../../api/core.js';
import MissionStatusBadge from './MissionStatusBadge.jsx';
import MissionFormModal from './MissionFormModal.jsx';
import MissionTimeline from './MissionTimeline.jsx';
import TrajetBuilder from './TrajetBuilder.jsx';
import { useFormat } from '../../../i18n/format.js';

const STATUS_FILTERS = [
  { id: '', label: 'Toutes' },
  { id: 'draft', label: 'Brouillon' },
  { id: 'assigned', label: 'Assignées' },
  { id: 'accepted', label: 'Acceptées' },
  { id: 'in_progress', label: 'En cours' },
  { id: 'completed', label: 'Terminées' },
  { id: 'cancelled', label: 'Annulées' },
];

export default function MissionManagerView({ onNavigate }) {
  const { fmt } = useFormat();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMission, setEditMission] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadMissions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { _limit: 100 };
      if (statusFilter) params.status = statusFilter;
      const res = await missionAPI.getAll(params);
      setMissions(res.data || []);
    } catch (err) {
      console.error('Failed to load missions:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  const loadMissionDetail = async (id) => {
    try {
      setDetailLoading(true);
      const res = await missionAPI.getById(id);
      setSelectedMission(res.data);
    } catch (err) {
      console.error('Failed to load mission detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAssign = async (missionId, driverId) => {
    try {
      await missionAPI.assign(missionId, { driver_id: driverId });
      loadMissions();
      if (selectedMission?.id === missionId) {
        loadMissionDetail(missionId);
      }
    } catch (err) {
      console.error('Failed to assign mission:', err);
    }
  };

  const handleDelete = async (missionId) => {
    if (!window.confirm('Supprimer cette mission brouillon ?')) return;
    try {
      await missionAPI.delete(missionId);
      setSelectedMission(null);
      loadMissions();
    } catch (err) {
      console.error('Failed to delete mission:', err);
    }
  };

  const handleSaved = () => {
    setShowCreateModal(false);
    setEditMission(null);
    loadMissions();
  };

  const handleDownloadFiche = (missionId) => {
    const headers = getAuthHeaders();
    const token = headers['Authorization']?.replace('Bearer ', '') || '';
    window.open(`${API_BASE_URL}/missions/${missionId}/fiche-pdf?token=${encodeURIComponent(token)}`, '_blank');
  };

  const filteredMissions = missions.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (m.title || '').toLowerCase().includes(q) ||
      (m.code || '').toLowerCase().includes(q) ||
      (m.client_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
              <Map className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Missions</h1>
              <p className="text-sm text-gray-500">{filteredMissions.length} mission{filteredMissions.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadMissions}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nouvelle mission
            </button>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par titre, code, client..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-1 overflow-x-auto">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  statusFilter === f.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content: List + Detail Side Panel */}
      <div className="flex gap-4">
        {/* Mission List */}
        <div className={`${selectedMission ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filteredMissions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <Map className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune mission trouvée</p>
            </div>
          ) : (
            filteredMissions.map(mission => (
              <div
                key={mission.id}
                onClick={() => loadMissionDetail(mission.id)}
                className={`bg-white rounded-lg shadow-sm border p-4 cursor-pointer hover:border-blue-300 hover:shadow transition-all ${
                  selectedMission?.id === mission.id ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-400">{mission.code}</span>
                      <MissionStatusBadge status={mission.status} />
                      {mission.priority === 'urgent' && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">Urgent</span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 truncate">{mission.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {mission.planned_start && (
                        <span>{fmt.date(mission.planned_start)}</span>
                      )}
                      {mission.client_name && <span>Client: {mission.client_name}</span>}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        {selectedMission && (
          <div className="w-1/2 bg-white rounded-lg shadow-sm border border-gray-200 p-5 sticky top-20 max-h-[80vh] overflow-y-auto">
            {detailLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-400">{selectedMission.code}</span>
                      <MissionStatusBadge status={selectedMission.status} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mt-1">{selectedMission.title}</h3>
                  </div>
                  <div className="flex gap-1">
                    {selectedMission.status === 'draft' && (
                      <>
                        <button
                          onClick={() => setEditMission(selectedMission)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Modifier"
                        >
                          <Edit2 className="h-4 w-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(selectedMission.id)}
                          className="p-2 hover:bg-red-50 rounded-lg"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDownloadFiche(selectedMission.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title="Fiche de Mission PDF"
                    >
                      <Download className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Assign button */}
                {selectedMission.status === 'draft' && !selectedMission.driver_id && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    Aucun conducteur assigné. Cliquez "Assigner" pour démarrer.
                  </div>
                )}

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedMission.driver && (
                    <div>
                      <p className="text-xs text-gray-500">Conducteur</p>
                      <p className="font-medium">{selectedMission.driver.first_name} {selectedMission.driver.last_name}</p>
                    </div>
                  )}
                  {selectedMission.vehicle && (
                    <div>
                      <p className="text-xs text-gray-500">Véhicule</p>
                      <p className="font-medium">{selectedMission.vehicle.registration_plate}</p>
                    </div>
                  )}
                  {selectedMission.client_name && (
                    <div>
                      <p className="text-xs text-gray-500">Client</p>
                      <p className="font-medium">{selectedMission.client_name}</p>
                    </div>
                  )}
                  {selectedMission.priority && (
                    <div>
                      <p className="text-xs text-gray-500">Priorité</p>
                      <p className="font-medium capitalize">{selectedMission.priority}</p>
                    </div>
                  )}
                  {selectedMission.planned_start && (
                    <div>
                      <p className="text-xs text-gray-500">Début</p>
                      <p className="font-medium">{fmt.dateTime(selectedMission.planned_start)}</p>
                    </div>
                  )}
                  {selectedMission.planned_end && (
                    <div>
                      <p className="text-xs text-gray-500">Fin</p>
                      <p className="font-medium">{fmt.dateTime(selectedMission.planned_end)}</p>
                    </div>
                  )}
                </div>

                {selectedMission.description && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-700">{selectedMission.description}</p>
                  </div>
                )}

                {/* Trajets */}
                <div className="border-t border-gray-200 pt-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Trajets ({(selectedMission.trajets || []).length})
                  </h4>
                  <MissionTimeline trajets={selectedMission.trajets || []} />
                </div>

                {/* Totals */}
                {selectedMission.total_km > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 flex gap-6 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Distance</p>
                      <p className="font-bold">{Math.round(selectedMission.total_km)} km</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editMission) && (
        <MissionFormModal
          mission={editMission}
          onClose={() => { setShowCreateModal(false); setEditMission(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
